/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { HttpClient } from '@angular/common/http'
import { AsyncPipe, NgClass } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  finalize,
  map,
  throwError,
} from 'rxjs'

//PRIMENG
import { ButtonModule } from 'primeng/button'
import { InputTextModule } from 'primeng/inputtext'
import { FloatLabelModule } from 'primeng/floatlabel'
import { DividerModule } from 'primeng/divider'
import { MenuItem } from 'primeng/api'
import { CardModule } from 'primeng/card'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { CheckboxModule } from 'primeng/checkbox'

//Leaflet
import { latLng, tileLayer, GeoJSON, geoJSON } from 'leaflet'
import { LeafletModule } from '@bluehalo/ngx-leaflet'
//APP
import { UserModel } from '../../../../core/models/user.model'
import {
  DEFAULT_WORK_HOURS,
  WorkDayKey,
  WorkHours,
} from '../../../../core/models/user.model'
import { ToastService } from '../../../../shared/services/toast.service'
import { UserService } from '../../services/user.service'
import { MarketTypesService } from '../../../markets-types/services/market-types.service'
import { ProductsService } from '../../../products/services/products.service'
import { MlSocieties } from '../../../../core/enums/ml-societies'
import { ProductTagComponent } from '../../../products/components/product-tag/product-tag.component'
import { ProductModel } from '../../../../core/models/product.model'
import { NavigationServiceService } from '../../../../core/services/navigation-service.service'

@Component({
  selector: 'app-edit-user',
  standalone: true,
  imports: [
    ButtonModule,
    InputTextModule,
    ReactiveFormsModule,
    FloatLabelModule,
    DividerModule,
    LeafletModule,
    AsyncPipe,
    NgClass,
    ProductTagComponent,
    CardModule,
    ProgressSpinnerModule,
    CheckboxModule,
  ],
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss',
})
export class EditUserComponent implements OnInit {
  private toastService = inject(ToastService)
  private fb = inject(FormBuilder)
  private userService = inject(UserService)
  private marketsTypesService = inject(MarketTypesService)
  private productService = inject(ProductsService)
  private http = inject(HttpClient)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private navigation = inject(NavigationServiceService)

  private routeUserId = +this.route.snapshot.params['id']
  user: UserModel | null = null
  options = {
    layers: [
      tileLayer(
        'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        {
          minZoom: 5,
          maxZoom: 10,
        }
      ),
    ],
    zoom: 6,
    center: latLng(46.303558, 2.0164252),
  }
  layers: GeoJSON[] = []
  userForm!: FormGroup
  loading = false
  selectedMarkType = new BehaviorSubject<number | null>(null)
  selectedMlSocietyId = new BehaviorSubject<String | null>(null)
  marketsTypes$ = this.marketsTypesService.marketsTypes$.pipe(
    map((marketsTypes) =>
      marketsTypes.map((marketType) => ({
        label: marketType.name,
        id: marketType.id.toString(),
      }))
    )
  )
  private marketsTypesArray: { label: string; id: string }[] = []
  mlSocieties = Object.values(MlSocieties).map((society) => {
    return { id: society, label: '', icon: 'tll tll-24 fa-logo-' + society }
  })
  products$ = this.productService.products$
  filteredProducts$ = combineLatest([
    this.selectedMarkType,
    this.selectedMlSocietyId,
    this.products$,
  ]).pipe(
    map(([marketTypeId, mlSociety, products]) => {
      return products.filter((product) => {
        if (!mlSociety || !marketTypeId) {
          return false
        }
        if (mlSociety === 'abm') {
          return product.marketsTypesIds.includes(marketTypeId) && product.idAbm
        } else if (mlSociety === 'nrj') {
          return product.marketsTypesIds.includes(marketTypeId) && product.idNrj
        } else if (mlSociety === 'bb') {
          return product.marketsTypesIds.includes(marketTypeId) && product.idBb
        } else {
          return false
        }
      })
    })
  )

  activeMarketTypeIndex = 0
  activeMlSocietyIndex = 0

  readonly workDays: { key: WorkDayKey; label: string }[] = [
    { key: 'monday', label: 'Lundi' },
    { key: 'tuesday', label: 'Mardi' },
    { key: 'wednesday', label: 'Mercredi' },
    { key: 'thursday', label: 'Jeudi' },
    { key: 'friday', label: 'Vendredi' },
    { key: 'saturday', label: 'Samedi' },
    { key: 'sunday', label: 'Dimanche' },
  ]

  constructor() {}

  ngOnInit() {
    this.userService
      .getUser(this.routeUserId)
      .pipe(
        catchError((error) => {
          this.router.navigate(['/users'])
          return throwError(() => new Error('User not found'))
        })
      )
      .subscribe((user) => {
        this.user = user
        this.initUserForm()
        this.initLeafletMap()
        // Initialiser les sélections après avoir chargé l'utilisateur
        this.initSelections()
      })
    this.productService.getProducts().subscribe()
    this.marketsTypesService.getMarketsTypes().subscribe()

    this.marketsTypes$.subscribe((marketTypes) => {
      this.marketsTypesArray = marketTypes
      // Initialiser le type de marché dès qu'on a les données
      if (marketTypes.length > 0 && !this.selectedMarkType.value) {
        this.selectMarketType(0)
      }
    })
  }

  // Nouvelle méthode pour initialiser les sélections
  initSelections(): void {
    // Initialiser le type de société ML
    if (this.mlSocieties.length > 0) {
      const mlIndex = this.getActiveMLSocietyIndex()
      this.selectMlSociety(mlIndex)
    }

    // Initialiser le type de marché si les données sont déjà disponibles
    if (this.marketsTypesArray.length > 0) {
      this.selectMarketType(0)
    }
  }

  initUserForm(): void {
    this.userForm = this.fb.group({
      firstname: [this.user?.firstname || ''],
      lastname: [this.user?.lastname || ''],
      initials: [this.user?.initials || ''],
      email: [this.user?.email || '', Validators.email],
      departments: [
        this.user?.departments.map((department) => department.code) || [],
      ],
      products: [this.user?.products.map((product) => product.id) || []],
      workHours: this.buildWorkHoursGroup(this.user?.workHours),
    })
  }

  private buildWorkHoursGroup(workHours?: WorkHours): FormGroup {
    const source: WorkHours = workHours ?? DEFAULT_WORK_HOURS
    const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/
    const days: Record<string, FormGroup> = {}
    this.workDays.forEach(({ key }) => {
      const day = source[key] ?? DEFAULT_WORK_HOURS[key]
      const dayGroup = this.fb.group(
        {
          start: [
            day.start,
            [Validators.required, Validators.pattern(timePattern)],
          ],
          end: [
            day.end,
            [Validators.required, Validators.pattern(timePattern)],
          ],
          enabled: [day.enabled],
          hotel: [day.hotel ?? false],
        },
        { validators: [this.endAfterStartValidator()] }
      )
      this.syncEnabledState(dayGroup)
      dayGroup
        .get('enabled')
        ?.valueChanges.subscribe(() => this.syncEnabledState(dayGroup))
      days[key] = dayGroup
    })
    return this.fb.group(days)
  }

  private syncEnabledState(dayGroup: FormGroup): void {
    const enabled = dayGroup.get('enabled')?.value
    const startCtrl = dayGroup.get('start')
    const endCtrl = dayGroup.get('end')
    if (enabled) {
      startCtrl?.enable({ emitEvent: false })
      endCtrl?.enable({ emitEvent: false })
    } else {
      startCtrl?.disable({ emitEvent: false })
      endCtrl?.disable({ emitEvent: false })
    }
  }

  private endAfterStartValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const start = group.get('start')?.value
      const end = group.get('end')?.value
      if (!start || !end) return null
      return start < end ? null : { endBeforeStart: true }
    }
  }

  getDayGroup(key: WorkDayKey): FormGroup {
    return this.userForm.get(['workHours', key]) as FormGroup
  }

  isDayEnabled(key: WorkDayKey): boolean {
    return !!this.getDayGroup(key).get('enabled')?.value
  }

  isHotelDay(key: WorkDayKey): boolean {
    return !!this.getDayGroup(key).get('hotel')?.value
  }

  resetWorkHoursToDefaults(): void {
    this.workDays.forEach(({ key }) => {
      const def = DEFAULT_WORK_HOURS[key]
      const group = this.getDayGroup(key)
      group.patchValue(
        { start: def.start, end: def.end, enabled: def.enabled, hotel: def.hotel },
        { emitEvent: false }
      )
      this.syncEnabledState(group)
      group.updateValueAndValidity()
    })
  }

  copyDayToWeekdays(sourceKey: WorkDayKey): void {
    const source = this.getDayGroup(sourceKey).getRawValue()
    const weekdays: WorkDayKey[] = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
    ]
    weekdays
      .filter((k) => k !== sourceKey)
      .forEach((key) => {
        const group = this.getDayGroup(key)
        group.patchValue(
          { start: source.start, end: source.end, enabled: source.enabled },
          { emitEvent: false }
        )
        this.syncEnabledState(group)
        group.updateValueAndValidity()
      })
  }

  get totalWeeklyHours(): number {
    if (!this.userForm) return 0
    let total = 0
    this.workDays.forEach(({ key }) => {
      const day = this.getDayGroup(key).getRawValue()
      if (!day.enabled || !day.start || !day.end) return
      const [sh, sm] = day.start.split(':').map(Number)
      const [eh, em] = day.end.split(':').map(Number)
      const diff = eh * 60 + em - (sh * 60 + sm)
      if (diff > 0) total += diff
    })
    return Math.round((total / 60) * 10) / 10
  }

  initLeafletMap(): void {
    this.http.get('france.json').subscribe((json: any) => {
      console.log(json)
      this.layers.push(
        geoJSON(json, {
          style: (feature) => {
            let color = '#60a5fa'
            if (
              this.userForm
                .getRawValue()
                .departments.includes(feature?.properties.code)
            ) {
              color = '#16a34a'
            }
            return { color: color, fillOpacity: 0.5, weight: 1 }
          },
          onEachFeature: (feature, layer) => {
            layer.on('mouseover', (e) => this.highlightFeature(e))
            layer.on('mouseout', (e) => this.resetHighlight(e))
            layer.on('click', (e) => this.toggleDepartment(e))
            layer.bindTooltip(feature.properties.nom, {
              opacity: 0.7,
              offset: [30, 30],
              direction: 'center',
            })
          },
        })
      )
    })

    console.log(this.layers)
    console.log(this.options)
  }

  submit() {
    this.loading = true
    if (this.user) {
      this.userService
        .updateUser(this.userForm.getRawValue(), this.user.id)
        .pipe(
          finalize(() => {
            this.loading = false
          })
        )
        .subscribe((response) => {
          this.toastService.showSuccess(response.message)
        })
    }
  }

  private highlightFeature(e: any): void {
    const layer = e.target
    layer.setStyle({ fillOpacity: 0.2 })
  }

  private resetHighlight(e: any): void {
    const layer = e.target
    layer.setStyle({ fillOpacity: 0.5 })
  }

  private toggleDepartment(e: any): void {
    const layer = e.target
    const departmentsControlValue = this.userForm.controls['departments'].value
    let updatedDepartments: string[] = []
    if (departmentsControlValue.includes(e.target.feature.properties.code)) {
      updatedDepartments = departmentsControlValue.filter(
        (department: string) => department !== e.target.feature.properties.code
      )
      layer.setStyle({ color: '#60a5fa' })
    } else {
      updatedDepartments = [
        ...departmentsControlValue,
        e.target.feature.properties.code,
      ]
      layer.setStyle({ color: '#16a34a' })
    }
    this.userForm.patchValue({
      departments: updatedDepartments,
    })
  }

  selectMarketType(index: number) {
    this.activeMarketTypeIndex = index
    if (this.marketsTypesArray && this.marketsTypesArray[index]) {
      const marketType = this.marketsTypesArray[index]
      if (marketType.id) {
        this.selectedMarkType.next(+marketType.id)
      }
    }
  }

  selectMlSociety(index: number) {
    this.activeMlSocietyIndex = index
    if (this.mlSocieties[index] && this.mlSocieties[index].id) {
      this.selectedMlSocietyId.next(this.mlSocieties[index].id)
    }
  }

  getActiveMLSocietyIndex(): number {
    if (!this.user || !this.mlSocieties.length) return 0

    if (this.user.idAbm) {
      const index = this.mlSocieties.findIndex((item) => item.id === 'abm')
      this.activeMlSocietyIndex = index >= 0 ? index : 0
      return this.activeMlSocietyIndex
    }
    if (this.user.idNrj) {
      const index = this.mlSocieties.findIndex((item) => item.id === 'nrj')
      this.activeMlSocietyIndex = index >= 0 ? index : 0
      return this.activeMlSocietyIndex
    }
    /*if (this.user.idBb) {
      const index = this.mlSocieties.findIndex((item) => item.id === 'bb')
      this.activeMlSocietyIndex = index >= 0 ? index : 0
      return this.activeMlSocietyIndex
    }*/
    return 0
  }

  isSelected(product: ProductModel): boolean {
    return this.userForm.controls['products'].value.includes(product.id)
  }

  addSelectedProduct(product: ProductModel) {
    let currentProduct = this.userForm.controls['products'].value
    this.userForm.controls['products'].patchValue([
      ...currentProduct,
      product.id,
    ])
  }
  removeSelectedProduct(product: ProductModel) {
    this.userForm.controls['products'].patchValue(
      this.userForm.controls['products'].value.filter(
        (productId: number) => productId != product.id
      )
    )
  }

  // Sélectionne tous les produits filtrés
  selectAllProducts(products: ProductModel[] | null): void {
    if (!products || products.length === 0) return

    const currentProducts = this.userForm.controls['products'].value
    const productIds = new Set([...currentProducts])

    // Ajouter tous les IDs des produits filtrés
    products.forEach((product) => {
      productIds.add(product.id)
    })

    this.userForm.controls['products'].patchValue(Array.from(productIds))
  }

  // Désélectionne tous les produits filtrés
  deselectAllProducts(products: ProductModel[] | null): void {
    if (!products || products.length === 0) return

    const currentProducts = this.userForm.controls['products'].value

    // Filtrer pour enlever les produits actuellement affichés
    const filteredProductIds = currentProducts.filter(
      (productId: number) =>
        !products.some((product) => product.id === productId)
    )

    this.userForm.controls['products'].patchValue(filteredProductIds)
  }

  getDefaultMlSociety(menuItems: MenuItem[], user: UserModel) {
    if (this.user?.idAbm) {
      return menuItems.find((menuItem) => menuItem.id === 'abm') || menuItems[0]
    }
    if (this.user?.idNrj) {
      return menuItems.find((menuItem) => menuItem.id === 'nrj') || menuItems[0]
    }
    if (this.user?.idBb) {
      return menuItems.find((menuItem) => menuItem.id === 'bb') || menuItems[0]
    }
    return menuItems[0]
  }
  back(): void {
    this.navigation.back()
  }
}
